import { Component, EventEmitter, HostListener, Input, OnDestroy, Output } from '@angular/core';
import { URLS } from '../../data/navigation/navigation.data';
import { PaletteSubtitle } from '../palette-widget/palette-widget.component';

type GradientType = 'linear' | 'radial' | 'conic';

// Sequential ids so two widgets on a page never share the file-input id.
let gradientWidgetSeq = 0;

// The gradient builder card. A fully standalone, controlled component: it renders
// the palette / type / angle it is given and emits intent events; the host owns
// the state and the URL. It is intentionally NOT built on top of the palette
// widget any more (that nesting caused a render flash and coupled the two tools).
@Component( {
  selector: 'app-gradient-widget',
  templateUrl: './gradient-widget.component.html',
  styleUrls: [ './gradient-widget.component.scss' ],
} )
export class GradientWidgetComponent implements OnDestroy {

  /** the preview open large over the page (owner 2026-10-12) */
  zoomed = false;

  @HostListener( 'document:keydown.escape' )
  closeZoom(): void {
    this.zoomed = false;
  }

  @Input() palette: { value: string }[] = [];
  @Input() subtitle: PaletteSubtitle | null = null;
  @Input() type: GradientType = 'linear';
  @Input() angle = 115;
  @Input() count = 10;
  @Input() positions: number[] = [];

  readonly title = 'Gradient Generator';
  // Home page: a "Open Full Gradient Tool" CTA under the card, like the palette
  // and color widgets.
  @Input() showDetailsCta = false;
  @Input() detailsRoute: string = URLS.tools.gradient;
  @Input() detailsQueryParams: { [ key: string ]: string } | null = null;
  readonly fileInputId = 'gradient-image-upload-' + ( ++gradientWidgetSeq );

  readonly types: { key: GradientType; label: string }[] = [
    { key: 'linear', label: 'Linear' },
    { key: 'radial', label: 'Radial' },
    { key: 'conic', label: 'Conic' },
  ];

  // The Initialize menu, in display order (same generators as the palette tool).
  readonly schemes: { key: string; label: string }[] = [
    { key: 'theme', label: 'Codigrate Themes' },
    { key: 'featured', label: 'Featured' },
    { key: 'complementary', label: 'Complementary' },
    { key: 'split complementary', label: 'Split Complementary' },
    { key: 'triadic', label: 'Triadic' },
    { key: 'tetradic', label: 'Tetradic' },
    { key: 'analogous', label: 'Analogous' },
    { key: 'monochrome', label: 'Monochrome' },
    { key: 'warm', label: 'Warm' },
    { key: 'cool', label: 'Cool' },
    { key: 'full', label: 'Full Random' },
  ];

  // Gradient count floors: a gradient needs only two stops, so most generators
  // allow 2; the schemes below need more. Themes are locked to 10.
  private readonly defaultMin = 2;
  private readonly schemeMins: { [ key: string ]: number } = {
    'split complementary': 3,
    triadic: 3,
    tetradic: 4,
  };

  @Output() editColor = new EventEmitter<{ index: number; value: string }>();
  @Output() removeColor = new EventEmitter<number>();
  /** the stops in the opposite order, their places mirrored (the host turns them around) */
  @Output() reverse = new EventEmitter<void>();
  @Output() generate = new EventEmitter<string>();
  @Output() uploadImage = new EventEmitter<Event>();
  @Output() typeChange = new EventEmitter<GradientType>();
  @Output() angleChange = new EventEmitter<number>();
  @Output() countChange = new EventEmitter<number>();
  @Output() positionChange = new EventEmitter<{ index: number; value: number }>();
  // A new stop dropped on the bar at a clicked position (0-100).
  @Output() insertStop = new EventEmitter<{ position: number }>();

  // ===================== Initialize form =====================

  selectedScheme = '';

  // The page presets the dropdown to the current source generator (e.g. on a
  // random landing it reflects "Triadic" while the Range shows the generated
  // count). Driven by an input, but a later user pick still wins until the page's
  // own source actually changes (the input only re-fires when its value changes).
  // a theme picked for Codigrate Themes in the generator picker ('' = any theme); the host presets it
  selectedTheme = '';

  @Input() set themeKey( v: string | undefined ) {
    this.selectedTheme = v || '';
  }

  @Input() set scheme( v: string ) {
    this.selectedScheme = v || '';
  }

  get countMin(): number {
    const m = this.schemeMins[ this.selectedScheme ];
    return m != null ? m : this.defaultMin;
  }

  get countDisabled(): boolean {
    return this.selectedScheme === 'theme' || this.selectedScheme === 'featured';
  }

  // The fixed colour count for a locked generator: Codigrate Themes always use 10
  // colours; a Featured gradient is a curated two-stop pair, so it locks to 2.
  get lockedCount(): number {
    return this.selectedScheme === 'featured' ? 2 : 10;
  }

  get countLockReason(): string {
    if ( this.selectedScheme === 'featured' ) {
      return 'Featured gradients are fixed to 2 stops';
    }
    return 'Codigrate Themes always use 10 colors';
  }

  get canGenerate(): boolean {
    return !!this.selectedScheme;
  }

  // Options for the custom dropdowns.
  get generatorOptions(): { value: string; label: string }[] {
    // Codigrate Themes first, From Image right after it, then the harmony schemes (owner, 2026-09-22)
    const [ first, ...rest ] = this.schemes.map( s => ( { value: s.key, label: s.label } ) );
    return [ first, { value: 'image', label: 'From Image' }, ...rest ];
  }

  get typeOptions(): { value: string; label: string }[] {
    return this.types.map( t => ( { value: t.key, label: t.label } ) );
  }

  onSchemeChange( key: string ): void {
    this.selectedScheme = key;
    if ( key === 'theme' && this.count !== 10 ) {
      this.count = 10;
      this.countChange.emit( 10 );
    } else if ( key === 'featured' && this.count !== 2 ) {
      // A Featured gradient is always a two-stop pair.
      this.count = 2;
      this.countChange.emit( 2 );
    } else if ( key !== 'theme' && key !== 'featured' && this.count < this.countMin ) {
      this.count = this.countMin;
      this.countChange.emit( this.count );
    }
  }

  onCountInput( el: HTMLInputElement ): void {
    el.value = ( el.value || '' ).replace( /[^0-9]/g, '' ).slice( 0, 2 );
  }

  onCountCommit( el: HTMLInputElement ): void {
    const n = Math.max( this.countMin, Math.min( 10, parseInt( el.value, 10 ) || this.countMin ) );
    this.count = n;
    el.value = String( n );
    this.countChange.emit( n );
  }

  // The up / down stepper buttons inside the count field.
  stepCount( delta: number ): void {
    if ( this.countDisabled ) {
      return;
    }
    const n = Math.max( this.countMin, Math.min( 10, this.count + delta ) );
    if ( n !== this.count ) {
      this.count = n;
      this.countChange.emit( n );
    }
  }

  // Press-and-hold on a stepper button: step once immediately, then auto-repeat
  // after a short delay until the pointer is released (or the bound is reached).
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private holdRepeat: ReturnType<typeof setInterval> | null = null;

  startStep( delta: number ): void {
    this.stopStep();
    this.stepCount( delta );
    this.holdTimer = setTimeout( () => {
      this.holdRepeat = setInterval( () => {
        const before = this.count;
        this.stepCount( delta );
        if ( this.count === before ) {
          this.stopStep();
        }
      }, 90 );
    }, 350 );
  }

  stopStep(): void {
    if ( this.holdTimer ) {
      clearTimeout( this.holdTimer );
      this.holdTimer = null;
    }
    if ( this.holdRepeat ) {
      clearInterval( this.holdRepeat );
      this.holdRepeat = null;
    }
  }

  ngOnDestroy(): void {
    this.stopStep();
  }

  onGenerateClick(): void {
    if ( !this.selectedScheme ) {
      return;
    }
    if ( this.selectedScheme === 'image' ) {
      const input = document.getElementById( this.fileInputId ) as HTMLInputElement | null;
      input?.click();
      return;
    }
    this.generate.emit( this.selectedScheme === 'theme' && this.selectedTheme ? 'theme:' + this.selectedTheme : this.selectedScheme );
  }

  // ===================== Subtitle helpers =====================

  get imageNameDisplay(): string {
    const raw = ( this.subtitle?.imageName || '' ).trim();
    if ( !raw ) {
      return 'uploaded image';
    }
    const max = 24;
    if ( raw.length <= max ) {
      return raw;
    }
    const dot = raw.lastIndexOf( '.' );
    const ext = dot > 0 ? raw.slice( dot ) : '';
    const tailKeep = Math.min( raw.length - 1, ext.length + 5 );
    const headKeep = Math.max( 1, max - tailKeep - 1 );
    return raw.slice( 0, headKeep ) + '…' + raw.slice( raw.length - tailKeep );
  }

  get schemeArticle(): string {
    return /^[aeiou]/i.test( ( this.subtitle?.scheme || '' ).trim() ) ? 'an' : 'a';
  }

  // ===================== Type / angle panel =====================

  get usesAngle(): boolean {
    return this.type !== 'radial';
  }

  // Turn the stops around; the same stop stays selected, at its mirrored place.
  reverseStops(): void {
    if ( this.palette.length < 2 ) return;
    this.activeStop = this.palette.length - 1 - this.idx;
    this.reverse.emit();
  }

  get idx(): number {
    return Math.max( 0, Math.min( this.activeStop, this.palette.length - 1 ) );
  }

  get selectedColor(): string {
    return this.palette[ this.idx ]?.value || '#000000';
  }

  get selectedHexLabel(): string {
    return '#' + this.selectedColor.replace( '#', '' ).toUpperCase();
  }

  get selectedPos(): number {
    return Math.round( this.stopLeft( this.idx ) );
  }

  // While typing: only hex characters behind a single '#', capped at 6 (no other
  // characters, no shorthand) — identical to the palette tool's Color input.
  // Apply a full, valid 6-digit hex live (don't wait for blur).
  onHexInput( el: HTMLInputElement ): void {
    const hex = ( el.value || '' ).replace( /[^0-9a-fA-F]/g, '' ).slice( 0, 6 );
    el.value = '#' + hex.toUpperCase();
    if ( /^[0-9a-fA-F]{6}$/.test( hex ) ) {
      this.editColor.emit( { index: this.idx, value: '#' + hex.toLowerCase() } );
    }
  }

  // Commit only a full 6-digit hex (no 3-digit shorthand); ignore anything else.
  commitHex( raw: string ): void {
    const v = ( raw || '' ).replace( /[^0-9a-fA-F]/g, '' );
    if ( /^[0-9a-fA-F]{6}$/.test( v ) ) {
      this.editColor.emit( { index: this.idx, value: '#' + v.toLowerCase() } );
    }
  }

  commitPos( raw: string ): void {
    const n = parseFloat( ( raw || '' ).replace( /[^0-9.]/g, '' ) );
    if ( !isNaN( n ) ) {
      this.positionChange.emit( { index: this.idx, value: Math.max( 0, Math.min( 100, n ) ) } );
    }
  }

  // Live-filter the Rotation field: digits only, never above 360. Letters and any
  // other characters are rejected as typed.
  onRotationInput( el: HTMLInputElement ): void {
    let v = ( el.value || '' ).replace( /[^0-9]/g, '' ).slice( 0, 3 );
    if ( v !== '' && parseInt( v, 10 ) > 360 ) { v = '360'; }
    el.value = v;
  }

  commitRotation( raw: string ): void {
    const n = parseInt( ( raw || '' ).replace( /[^0-9]/g, '' ), 10 );
    if ( !isNaN( n ) ) {
      this.angleChange.emit( Math.max( 0, Math.min( 360, n ) ) );
    }
  }

  // ===================== Interactive bar (74px square stops) =====================

  // The stop currently selected on the bar (its colour / position fill the panel).
  activeStop = 0;
  private dragIndex = -1;
  private barEl: HTMLElement | null = null;

  // The thin track behind the chips, the palette spread left-to-right.
  barGradient(): string {
    const cols = this.palette.map( p => p.value ).filter( Boolean );
    if ( cols.length === 0 ) {
      return 'transparent';
    }
    if ( cols.length === 1 ) {
      return cols[ 0 ];
    }
    const stops = cols.map( ( c, i ) => `${ c } ${ this.stopLeft( i ).toFixed( 2 ) }%` ).join( ', ' );
    return `linear-gradient(90deg, ${ stops })`;
  }

  // The gradient itself, as the tool would export it (its type, its angle), for the preview in the
  // panel's last third (owner 2026-10-11: the result at the bottom right, as the wallpaper tool shows it).
  previewGradient(): string {
    const cols = this.palette.map( p => p.value ).filter( Boolean );
    if ( cols.length < 2 ) {
      return cols[ 0 ] || 'transparent';
    }
    const stops = cols.map( ( c, i ) => `${ c } ${ this.stopLeft( i ).toFixed( 2 ) }%` ).join( ', ' );
    if ( this.type === 'radial' ) {
      return `radial-gradient(circle, ${ stops })`;
    }
    if ( this.type === 'conic' ) {
      return `conic-gradient(from ${ this.angle }deg, ${ stops })`;
    }
    return `linear-gradient(${ this.angle }deg, ${ stops })`;
  }

  // A stop's position along the track (even spacing until positions catch up).
  stopLeft( i: number ): number {
    const n = this.palette.length;
    if ( this.positions.length === n ) {
      return this.positions[ i ];
    }
    return n > 1 ? ( i / ( n - 1 ) ) * 100 : 0;
  }

  private pctFromEvent( event: { clientX: number }, bar: HTMLElement ): number {
    const rect = bar.getBoundingClientRect();
    return Math.max( 0, Math.min( 100, ( ( event.clientX - rect.left ) / rect.width ) * 100 ) );
  }

  startStopDrag( event: PointerEvent, index: number, bar: HTMLElement ): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragIndex = index;
    this.activeStop = index;
    this.barEl = bar;
    ( event.target as HTMLElement ).setPointerCapture?.( event.pointerId );
  }

  onStopDragMove( event: PointerEvent ): void {
    if ( this.dragIndex < 0 || !this.barEl ) {
      return;
    }
    this.positionChange.emit( { index: this.dragIndex, value: this.pctFromEvent( event, this.barEl ) } );
  }

  onStopDragEnd(): void {
    this.dragIndex = -1;
    this.barEl = null;
  }

  // Double-click a handle to delete that stop (min two stops stay).
  removeStop( index: number ): void {
    if ( this.palette.length <= 2 ) {
      return;
    }
    this.removeColor.emit( index );
    this.activeStop = Math.max( 0, Math.min( index, this.palette.length - 2 ) );
  }

  // Remove the selected stop via the editor's Remove button.
  removeSelected(): void {
    this.removeStop( this.idx );
  }

  onBarClick( event: MouseEvent, bar: HTMLElement ): void {
    if ( event.target !== bar || this.palette.length >= 10 ) {
      return;
    }
    const pos = this.pctFromEvent( event, bar );
    let i = 0;
    while ( i < this.palette.length && this.stopLeft( i ) < pos ) {
      i++;
    }
    this.activeStop = i;
    this.insertStop.emit( { position: pos } );
  }

}
